<script setup lang="ts">
import { computed } from "vue";
import { useResultsV2CardsStore } from "@stores/survey/resultsV2/resultsV2.cards";
import type { ChartCard, ResultsCard } from "@/types";
import OptionRow from "./OptionRow.vue";
import { Select } from "@/components/episto-ui/select";
import { useAnalysisContext } from "../analysisData";

const SCORE_TYPES = ["q_single", "q_rating", "q_nps", "q_slider"];

const props = withDefaults(
  defineProps<{
    card: Pick<ResultsCard, "id" | "content">;
    scoreLabel?: string;
    scoreHint?: string;
    scorePlaceholder?: string;
    matrixLabel?: string;
    matrixHint?: string;
    matrixPlaceholder?: string;
  }>(),
  {
    scoreLabel: "Score to explain",
    scoreHint: "The score the aspects explain: global satisfaction, NPS, purchase intent...",
    scorePlaceholder: "Global satisfaction, NPS, intent...",
    matrixLabel: "Aspects (matrix)",
    matrixHint: "The matrix whose items are the aspects, one per row.",
    matrixPlaceholder: "Select the matrix of aspects...",
  },
);

const cardsStore = useResultsV2CardsStore();
const { availableCards } = useAnalysisContext();

const scoreOptions = computed(() =>
  (availableCards.value as ChartCard[]).flatMap((c) => {
    const id = c.questions?.[0]?.id;
    return SCORE_TYPES.includes(c.type || "") && id != null ? [{ value: id, label: c.name || c.token }] : [];
  }),
);

const matrixOptions = computed(() =>
  (availableCards.value as ChartCard[]).flatMap((c) =>
    c.type === "q_matrix" && c.specs?.matrix_type !== "multiple" && c.id != null
      ? [{ value: c.id, label: c.name || c.token, ids: (c.questions || []).map((q) => q.id) }]
      : [],
  ),
);

function setContent(patch: Record<string, unknown>) {
  cardsStore.updateCardLocal(props.card.id, { content: { ...props.card.content, ...patch } });
}

const score = computed({
  get: () => (props.card.content?.global_question_id as number | undefined) || null,
  set: (value: number | null) => setContent({ global_question_id: value }),
});

const matrix = computed({
  get: () => {
    const ids = (props.card.content?.matrix_question_ids as number[] | undefined) || [];
    return matrixOptions.value.find((option) => option.ids.some((id) => ids.includes(id)))?.value || null;
  },
  set: (value: number | null) => setContent({ matrix_question_ids: matrixOptions.value.find((option) => option.value === value)?.ids || [] }),
});
</script>

<template>
  <OptionRow
    :label="scoreLabel"
    :hint="scoreHint">
    <Select
      :model-value="score"
      :options="scoreOptions"
      :placeholder="scorePlaceholder"
      filterable
      @update:model-value="score = $event as number | null" />
  </OptionRow>

  <OptionRow
    :label="matrixLabel"
    :hint="matrixHint">
    <Select
      :model-value="matrix"
      :options="matrixOptions"
      :placeholder="matrixPlaceholder"
      filterable
      @update:model-value="matrix = $event as number | null" />
  </OptionRow>
</template>
